import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { Box, Button, Chip, IconButton, Menu, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography, Container, Tooltip } from "@mui/material";
import {
    MoreVert,
    Event as EventIcon,
    Check,
    AccessTime,
    People as PeopleIcon,
    FiberManualRecord,
    History as HistoryIcon,
    Schedule,
    DoNotDisturbAlt,
    ExpandMore,
    ChevronRight,
    VisibilityOff,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getMeetings, setMeetingManualTrack } from "../redux/ducks/calendar";
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils";
import { toLocalDay } from "../utils/dateUtils";
import MeetingTimeEntryDialog from "../components/MeetingTimeEntryDialog";
import PageHeader from "../components/PageHeader";
import AppDatePicker from "../components/AppDatePicker";
import StatCard from "../components/StatCard";

// ── Layout shell ─────────────────────────────────────────────────────────────

const ContainerStyled = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3, 2),
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
}));

// ── Stats ────────────────────────────────────────────────────────────────────

const StatStrip = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
}));

// ── Filters ──────────────────────────────────────────────────────────────────

const FiltersBar = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const ChipRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
}));

const ChipLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: theme.palette.text.secondary,
    minWidth: 72,
}));

const DateRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${theme.palette.divider}`,
}));

const DateField = styled(Box)({
    width: 170,
    "& .MuiInputBase-input": {
        fontVariantNumeric: "tabular-nums",
    },
});

// ── Day section ──────────────────────────────────────────────────────────────

const DaySection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2.5),
}));

const DayHeader = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$tone" && prop !== "$clickable",
})(({ theme, $tone, $clickable }) => {
    const accentMap = {
        today: theme.palette.primary.main,
        tomorrow: theme.palette.text.primary,
        future: theme.palette.text.disabled,
        past: theme.palette.text.disabled,
    };
    const accent = accentMap[$tone] || theme.palette.text.disabled;
    return {
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 0.5, 0.75, 1.25),
        marginBottom: theme.spacing(0.75),
        background: `linear-gradient(${theme.palette.background.default} 60%, ${theme.palette.background.default}00)`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: $clickable ? "pointer" : "default",
        userSelect: $clickable ? "none" : "auto",
        transition: "background 0.15s ease",
        "&:hover": $clickable
            ? {
                  "& .day-chevron": {
                      color: theme.palette.text.primary,
                  },
                  "& .day-counts strong": {
                      color: theme.palette.primary.main,
                  },
              }
            : {},
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: theme.spacing(1.25),
            bottom: theme.spacing(1),
            width: 2,
            borderRadius: 1,
            background: accent,
        },
    };
});

const DayHeaderRight = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
}));

const DayChevron = styled(Box)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.disabled,
    transition: "color 0.15s ease",
    "& .MuiSvgIcon-root": {
        fontSize: theme.typography.size.base,
    },
}));

const DayLabel = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1.25),
    minWidth: 0,
}));

const DayPrimary = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: $tone === "today" ? theme.palette.primary.main : theme.palette.text.primary,
}));

const DaySecondary = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
}));

const DayCounts = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.04em",
    "& strong": {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
        transition: "color 0.15s ease",
    },
    "& .urgent": {
        color: theme.palette.warning.dark,
    },
}));

const FoldedHint = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.25, 0, 0.75, 1.25),
    borderLeft: `1px dashed ${theme.palette.divider}`,
    marginBottom: theme.spacing(0.75),
}));

const HandledToggle = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    margin: theme.spacing(0, 0, 2),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 999,
    border: `1px dashed ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    cursor: "pointer",
    userSelect: "none",
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
    "& .MuiSvgIcon-root": {
        fontSize: theme.typography.size.md,
    },
    "&:hover": {
        color: theme.palette.text.primary,
        borderColor: theme.palette.text.disabled,
        background: theme.palette.action.hover,
    },
    "& strong": {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightBold,
    },
}));

// ── Now divider ──────────────────────────────────────────────────────────────

const NowDivider = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(0.5, 1),
    "& .now-line": {
        flex: 1,
        height: 1,
        background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.main}33)`,
    },
    "& .now-label": {
        fontSize: theme.typography.size.xxs,
        fontWeight: theme.typography.fontWeightBold,
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        color: theme.palette.error.main,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
    },
    "& .now-dot": {
        fontSize: theme.typography.size.xs,
        color: theme.palette.error.main,
    },
}));

// ── Meeting row ──────────────────────────────────────────────────────────────

const Row = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$state" && prop !== "$cancelled" && prop !== "$past" && prop !== "$live",
})(({ theme, $state, $cancelled, $past, $live }) => {
    let leftAccent = "transparent";
    if ($live) leftAccent = theme.palette.error.main;
    else if ($cancelled) leftAccent = theme.palette.text.disabled;
    else if ($state === "tracked") leftAccent = theme.palette.success.main;
    else if ($state === "elsewhere") leftAccent = theme.palette.info.main;
    else if ($state === "untracked" && $past) leftAccent = theme.palette.warning.main;
    else leftAccent = theme.palette.divider;

    return {
        display: "grid",
        gridTemplateColumns: "92px 1fr auto auto",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${leftAccent}`,
        background: theme.palette.background.paper,
        marginBottom: theme.spacing(0.75),
        opacity: $cancelled ? 0.55 : $state === "tracked" || $state === "elsewhere" ? 0.78 : 1,
        transition: "background 0.15s ease, opacity 0.15s ease",
        "&:hover": {
            background: theme.palette.action.hover,
            opacity: 1,
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "72px 1fr auto",
            gridTemplateAreas: `
                "time main action"
                "time status action"
            `,
        },
    };
});

const TimeCol = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: { gridArea: "time" },
}));

const TimePrimary = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$cancelled",
})(({ theme, $cancelled }) => ({
    fontSize: theme.typography.size.base,
    fontWeight: theme.typography.fontWeightSemiBold,
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    textDecoration: $cancelled ? "line-through" : "none",
}));

const TimeSecondary = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: "0.02em",
}));

const MainCol = styled(Box)(({ theme }) => ({
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    [theme.breakpoints.down("sm")]: { gridArea: "main" },
}));

const MeetingTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "$cancelled",
})(({ theme, $cancelled }) => ({
    fontSize: theme.typography.size.base,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textDecoration: $cancelled ? "line-through" : "none",
    fontStyle: $cancelled ? "italic" : "normal",
}));

const MeetingMeta = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    "& > span": {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontVariantNumeric: "tabular-nums",
    },
    "& .MuiSvgIcon-root": {
        fontSize: theme.typography.size.md,
    },
    [theme.breakpoints.down("sm")]: { gridArea: "meta" },
}));

const RelativeTime = styled("span", {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => {
    const toneMap = {
        live: theme.palette.error.main,
        soon: theme.palette.warning.dark,
        past: theme.palette.text.disabled,
        future: theme.palette.text.secondary,
    };
    return {
        color: toneMap[$tone] || theme.palette.text.secondary,
        fontWeight: $tone === "live" || $tone === "soon" ? theme.typography.fontWeightSemiBold : theme.typography.fontWeightMedium,
        fontSize: theme.typography.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
    };
});

const LiveDot = styled(FiberManualRecord)(({ theme }) => ({
    fontSize: 8,
    marginRight: theme.spacing(0.5),
}));

const StatusCol = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    [theme.breakpoints.down("sm")]: { gridArea: "status", flexWrap: "wrap", minWidth: 0 },
}));

const ActionCol = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    justifySelf: "end",
    [theme.breakpoints.down("sm")]: { gridArea: "action" },
}));

const CategoryBadge = styled(Chip, { shouldForwardProp: (prop) => prop !== "$category" })(({ theme, $category }) => {
    const palette = {
        internal: theme.palette.info,
        client: theme.palette.success,
        misc: theme.palette.warning,
    };
    const c = palette[$category] || palette.misc;
    return {
        height: 20,
        fontSize: theme.typography.size.xxs,
        fontWeight: theme.typography.fontWeightBold,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        backgroundColor: c.main,
        color: c.contrastText,
        border: "none",
        "& .MuiChip-label": {
            paddingLeft: 8,
            paddingRight: 8,
        },
    };
});

const StatePill = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$tone",
})(({ theme, $tone }) => {
    const toneMap = {
        tracked: {
            bg: theme.palette.success.main,
            fg: theme.palette.success.contrastText,
        },
        elsewhere: {
            bg: theme.palette.info.main,
            fg: theme.palette.info.contrastText,
        },
        cancelled: {
            bg: theme.palette.action.hover,
            fg: theme.palette.text.secondary,
        },
    };
    const t = toneMap[$tone] || toneMap.cancelled;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: theme.spacing(0.25, 1),
        borderRadius: 999,
        fontSize: theme.typography.size.xs,
        fontWeight: theme.typography.fontWeightSemiBold,
        letterSpacing: "0.04em",
        backgroundColor: t.bg,
        color: t.fg,
        "& .MuiSvgIcon-root": {
            fontSize: theme.typography.size.sm,
            color: t.fg,
        },
    };
});

const RecordButton = styled(Button)(({ theme }) => ({
    height: 28,
    padding: theme.spacing(0, 1.5),
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: "0.02em",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
}));

// ── Empty / loading ──────────────────────────────────────────────────────────

const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    "& .MuiSvgIcon-root": {
        fontSize: theme.typography.h2.fontSize,
        color: theme.palette.text.disabled,
    },
}));

const SkeletonRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.75),
    background: theme.palette.background.paper,
}));

const ErrorBanner = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.error.main}`,
    background: `${theme.palette.error.main}0d`,
    color: theme.palette.error.dark,
    borderRadius: theme.shape.borderRadius,
    fontSize: theme.typography.size.smd,
}));

const ScheduleIcon = styled(Schedule)(({theme}) => ({
    fontSize: theme.typography.size.base,
}));

const AutoScrollHint = styled(Box)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.xs,
    letterSpacing: "0.04em",
}));

const menuItemIconStyle = ({ theme }) => ({ marginRight: theme.spacing(1) });
const TrackedElsewhereIcon = styled(HistoryIcon)(menuItemIconStyle);
const UntrackedMenuIcon = styled(AccessTime)(menuItemIconStyle);

// ── Constants & helpers ──────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
    { value: "all", label: "All" },
    { value: "internal", label: "Internal" },
    { value: "client", label: "Client" },
    { value: "misc", label: "Misc" },
];

const TRACKED_OPTIONS = [
    { value: "all", label: "All" },
    { value: "untracked", label: "Untracked" },
    { value: "recorded", label: "Tracked" },
    { value: "elsewhere", label: "Tracked Elsewhere" },
];

const defaultFromDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDay(d);
};

const defaultToDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return toLocalDay(d);
};

const dayKey = (date) => toLocalDay(date);

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const dayLabel = (date, today) => {
    const k = dayKey(date);
    const todayKey = dayKey(today);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (k === todayKey) return { primary: "Today", tone: "today" };
    if (k === dayKey(tomorrow)) return { primary: "Tomorrow", tone: "tomorrow" };
    if (k === dayKey(yesterday)) return { primary: "Yesterday", tone: "past" };
    const tone = date < today ? "past" : "future";
    return { primary: DAY_NAMES[date.getDay()], tone };
};

const dayDateLine = (date) => `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

const formatTime = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const formatDurationShort = (start, end) => {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return "";
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h${m}m`;
};

const meetingState = (m) => {
    if (m.timeEntryId) return "tracked";
    if (m.manuallyTracked) return "elsewhere";
    return "untracked";
};

const relativeTime = (start, end, now) => {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    const n = now.getTime();
    if (n >= s && n < e) return { label: "Live now", tone: "live" };
    if (n >= e) {
        const diffMin = Math.round((n - e) / 60000);
        if (diffMin < 60) return { label: `${diffMin}m ago`, tone: "past" };
        const diffH = Math.round(diffMin / 60);
        if (diffH < 24) return { label: `${diffH}h ago`, tone: "past" };
        const diffD = Math.round(diffH / 24);
        return { label: `${diffD}d ago`, tone: "past" };
    }
    const diffMin = Math.round((s - n) / 60000);
    if (diffMin < 60) return { label: `in ${diffMin}m`, tone: "soon" };
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return { label: `in ${diffH}h`, tone: diffH <= 2 ? "soon" : "future" };
    const diffD = Math.round(diffH / 24);
    return { label: `in ${diffD}d`, tone: "future" };
};

const Meetings = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const calendarStatus = useSelector((state) => state.calendar.status);
    const calendarRefresh = useSelector((state) => state.refreshTriggers.calendar);

    const [meetingsState, setMeetingsState] = useState({ items: [], total: 0, loading: false, error: null });

    const [category, setCategory] = useState("all");
    const [tracked, setTracked] = useState("all");
    const [from, setFrom] = useState(defaultFromDate());
    const [to, setTo] = useState(defaultToDate());

    const [timeDialogOpen, setTimeDialogOpen] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuMeeting, setMenuMeeting] = useState(null);

    const [expandedDays, setExpandedDays] = useState(() => new Set());
    const [showHandled, setShowHandled] = useState(false);

    const nowAnchorRef = useRef(null);
    const didScrollRef = useRef(false);

    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(id);
    }, []);

    const canSee = calendarStatus.loaded && hasPermission("canSeeInternal") && !calendarStatus.optOut;

    const queryParams = useMemo(
        () => ({
            from,
            to,
            category: category === "all" ? undefined : category,
            tracked,
            page: 1,
            limit: 200,
        }),
        [from, to, category, tracked],
    );

    useEffect(() => {
        if (!canSee) return;
        let cancelled = false;
        setMeetingsState((prev) => ({ ...prev, loading: true, error: null }));
        didScrollRef.current = false;
        setExpandedDays(new Set());
        setShowHandled(false);
        dispatch(
            getMeetings(
                queryParams,
                (data) => {
                    if (cancelled) return;
                    setMeetingsState({ items: data?.meetings || [], total: data?.total || 0, loading: false, error: null });
                },
                (error) => {
                    if (cancelled) return;
                    setMeetingsState((prev) => ({
                        ...prev,
                        loading: false,
                        error: error?.response?.data?.message || error?.message || "Failed to load meetings",
                    }));
                },
            ),
        );
        return () => {
            cancelled = true;
        };
    }, [dispatch, canSee, queryParams, calendarRefresh]);

    const handleRecordClick = (meeting) => {
        setSelectedMeeting(meeting);
        setTimeDialogOpen(true);
    };

    const handleTimeDialogClose = (result) => {
        setTimeDialogOpen(false);
        setSelectedMeeting(null);
        if (result) {
            dispatch(triggerRefresh({ triggerName: "calendar" }));
        }
    };

    const handleMenuOpen = (event, meeting) => {
        setMenuAnchor(event.currentTarget);
        setMenuMeeting(meeting);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuMeeting(null);
    };

    const handleManualTrack = (meeting, value) => {
        handleMenuClose();
        dispatch(
            setMeetingManualTrack(
                meeting._id,
                value,
                () => {
                    dispatch(showAlert({ severity: "success", message: value ? "Marked as tracked elsewhere" : "Marked as untracked" }));
                    dispatch(triggerRefresh({ triggerName: "calendar" }));
                },
                null,
            ),
        );
    };

    const items = meetingsState.items || [];
    const isLoading = meetingsState.loading;
    const hasError = !!meetingsState.error;

    const todayMidnight = useMemo(() => {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [now]);

    const stats = useMemo(() => {
        let untracked = 0;
        let trackedCount = 0;
        let elsewhereCount = 0;
        let cancelledCount = 0;
        let trackedHours = 0;
        items.forEach((m) => {
            if (m.cancelled) {
                cancelledCount += 1;
                return;
            }
            const state = meetingState(m);
            if (state === "tracked") {
                trackedCount += 1;
                const ms = new Date(m.end).getTime() - new Date(m.start).getTime();
                if (Number.isFinite(ms) && ms > 0) trackedHours += ms / 3600000;
            } else if (state === "elsewhere") {
                elsewhereCount += 1;
            } else {
                untracked += 1;
            }
        });
        return { untracked, trackedCount, elsewhereCount, cancelledCount, trackedHours };
    }, [items]);

    const grouped = useMemo(() => {
        const map = new Map();
        items.forEach((m) => {
            const start = new Date(m.start);
            if (Number.isNaN(start.getTime())) return;
            const key = dayKey(start);
            if (!map.has(key)) map.set(key, { date: new Date(start.getFullYear(), start.getMonth(), start.getDate()), items: [] });
            map.get(key).items.push(m);
        });
        const groups = Array.from(map.values()).sort((a, b) => a.date - b.date);
        groups.forEach((g) => g.items.sort((a, b) => new Date(a.start) - new Date(b.start)));
        return groups;
    }, [items]);

    useEffect(() => {
        if (didScrollRef.current) return;
        if (isLoading || hasError || items.length === 0) return;
        if (!nowAnchorRef.current) return;
        nowAnchorRef.current.scrollIntoView({ behavior: "auto", block: "center" });
        didScrollRef.current = true;
    }, [isLoading, hasError, items, grouped]);

    const renderRow = (meeting) => {
        const start = new Date(meeting.start);
        const end = new Date(meeting.end);
        const state = meetingState(meeting);
        const isLive = !meeting.cancelled && now >= start && now < end;
        const isPast = now >= end;
        const rel = relativeTime(meeting.start, meeting.end, now);

        return (
            <Row key={meeting._id} $state={state} $cancelled={meeting.cancelled} $past={isPast} $live={isLive}>
                <TimeCol>
                    <TimePrimary $cancelled={meeting.cancelled}>{formatTime(start)}</TimePrimary>
                    <TimeSecondary>
                        →&nbsp;{formatTime(end)} · {formatDurationShort(meeting.start, meeting.end)}
                    </TimeSecondary>
                </TimeCol>
                <MainCol>
                    <MeetingTitle $cancelled={meeting.cancelled} title={meeting.title}>
                        {meeting.title || "(no title)"}
                    </MeetingTitle>
                    <MeetingMeta>
                        <RelativeTime $tone={isLive ? "live" : rel.tone}>
                            {isLive && <LiveDot />}
                            {rel.label}
                        </RelativeTime>
                        <span>
                            <PeopleIcon /> {(meeting.attendees || []).length}
                        </span>
                        {meeting.cancelled && (
                            <span>
                                <DoNotDisturbAlt /> Cancelled
                            </span>
                        )}
                    </MeetingMeta>
                </MainCol>
                <StatusCol>
                    <CategoryBadge label={meeting.category || "misc"} $category={meeting.category || "misc"} size="small" />
                    {state === "tracked" && (
                        <StatePill $tone="tracked">
                            <Check /> Tracked
                        </StatePill>
                    )}
                    {state === "elsewhere" && (
                        <StatePill $tone="elsewhere">
                            <Check /> Elsewhere
                        </StatePill>
                    )}
                </StatusCol>
                <ActionCol>
                    {!meeting.cancelled && state === "untracked" && (
                        <RecordButton variant="contained" size="small" onClick={() => handleRecordClick(meeting)}>
                            Record time
                        </RecordButton>
                    )}
                    {(state === "untracked" || state === "elsewhere") && !meeting.cancelled && (
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, meeting)} aria-label="more">
                            <MoreVert fontSize="small" />
                        </IconButton>
                    )}
                </ActionCol>
            </Row>
        );
    };

    const classifyGroup = (group) => {
        const date = group.date;
        const todayK = dayKey(todayMidnight);
        const k = dayKey(date);
        const tomorrow = new Date(todayMidnight);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const yesterday = new Date(todayMidnight);
        yesterday.setDate(yesterday.getDate() - 1);
        const isToday = k === todayK;
        const isTomorrow = k === dayKey(tomorrow);
        const isYesterday = k === dayKey(yesterday);
        const isPast = date.getTime() < todayMidnight.getTime();
        const nonCancelled = group.items.filter((m) => !m.cancelled);
        const fullyHandled =
            nonCancelled.length === 0 || nonCancelled.every((m) => meetingState(m) !== "untracked");
        const hidden = isPast && fullyHandled;
        const folded = isPast && !isYesterday;
        return { isToday, isTomorrow, isYesterday, isPast, hidden, folded };
    };

    const toggleDay = (key) => {
        setExpandedDays((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const renderDayGroup = (group) => {
        const { date, items: dayItems } = group;
        const label = dayLabel(date, todayMidnight);
        const isToday = label.tone === "today";
        const k = dayKey(date);
        const cls = classifyGroup(group);
        const userExpanded = expandedDays.has(k);
        const foldable = cls.folded;
        const collapsed = foldable && !userExpanded;
        const dayUntracked = dayItems.filter((m) => !m.cancelled && meetingState(m) === "untracked").length;
        const dayTracked = dayItems.filter((m) => !m.cancelled && (meetingState(m) === "tracked" || meetingState(m) === "elsewhere")).length;
        const dayCancelled = dayItems.filter((m) => m.cancelled).length;

        let pastItems = [];
        let futureItems = [];
        if (isToday) {
            dayItems.forEach((m) => {
                const e = new Date(m.end).getTime();
                if (e <= now.getTime()) pastItems.push(m);
                else futureItems.push(m);
            });
        } else {
            futureItems = dayItems;
        }

        return (
            <DaySection key={k}>
                <DayHeader
                    $tone={label.tone}
                    $clickable={foldable}
                    onClick={foldable ? () => toggleDay(k) : undefined}
                    role={foldable ? "button" : undefined}
                    aria-expanded={foldable ? !collapsed : undefined}
                >
                    <DayLabel>
                        <DayPrimary $tone={label.tone}>{label.primary}</DayPrimary>
                        <DaySecondary>{dayDateLine(date)}</DaySecondary>
                    </DayLabel>
                    <DayHeaderRight>
                        <DayCounts className="day-counts">
                            {dayUntracked > 0 && (
                                <>
                                    <strong className="urgent">{dayUntracked}</strong> untracked
                                </>
                            )}
                            {dayUntracked > 0 && (dayTracked > 0 || dayCancelled > 0) && " · "}
                            {dayTracked > 0 && (
                                <>
                                    <strong>{dayTracked}</strong> tracked
                                </>
                            )}
                            {dayTracked > 0 && dayCancelled > 0 && " · "}
                            {dayCancelled > 0 && (
                                <>
                                    <strong>{dayCancelled}</strong> cancelled
                                </>
                            )}
                            {dayItems.length === 0 && "nothing scheduled"}
                        </DayCounts>
                        {foldable && (
                            <DayChevron className="day-chevron">
                                {collapsed ? <ChevronRight /> : <ExpandMore />}
                            </DayChevron>
                        )}
                    </DayHeaderRight>
                </DayHeader>
                {collapsed ? (
                    <FoldedHint>
                        {dayUntracked > 0
                            ? `${dayUntracked} untracked meeting${dayUntracked === 1 ? "" : "s"} · click to expand`
                            : "click to expand"}
                    </FoldedHint>
                ) : (
                    <>
                        {pastItems.map(renderRow)}
                        {isToday && (
                            <NowDivider ref={nowAnchorRef}>
                                <span className="now-label">
                                    <FiberManualRecord className="now-dot" /> Now · {formatTime(now)}
                                </span>
                                <span className="now-line" />
                            </NowDivider>
                        )}
                        {futureItems.map(renderRow)}
                    </>
                )}
            </DaySection>
        );
    };

    const todayInRange = useMemo(() => {
        if (grouped.length === 0) return false;
        return grouped.some((g) => dayKey(g.date) === dayKey(todayMidnight));
    }, [grouped, todayMidnight]);

    const groupsWithVirtualToday = useMemo(() => {
        if (todayInRange || grouped.length === 0) return grouped;
        const todayMs = todayMidnight.getTime();
        const hasPast = grouped.some((g) => g.date < todayMidnight);
        const hasFuture = grouped.some((g) => g.date > todayMidnight);
        if (!hasPast || !hasFuture) return grouped;
        const out = [];
        let inserted = false;
        grouped.forEach((g, i) => {
            if (!inserted && g.date.getTime() > todayMs) {
                out.push({ date: new Date(todayMidnight), items: [], virtualToday: true });
                inserted = true;
            }
            out.push(g);
        });
        return out;
    }, [grouped, todayInRange, todayMidnight]);

    const hiddenGroups = useMemo(() => {
        return groupsWithVirtualToday.filter((g) => {
            if (g.virtualToday) return false;
            const d = g.date;
            const isYesterday = (() => {
                const y = new Date(todayMidnight);
                y.setDate(y.getDate() - 1);
                return dayKey(d) === dayKey(y);
            })();
            const isPast = d.getTime() < todayMidnight.getTime();
            const nonCancelled = g.items.filter((m) => !m.cancelled);
            const fullyHandled =
                nonCancelled.length === 0 || nonCancelled.every((m) => meetingState(m) !== "untracked");
            return isPast && fullyHandled;
        });
    }, [groupsWithVirtualToday, todayMidnight]);

    const visibleGroups = useMemo(() => {
        if (showHandled) return groupsWithVirtualToday;
        const hiddenSet = new Set(hiddenGroups.map((g) => dayKey(g.date)));
        return groupsWithVirtualToday.filter((g) => g.virtualToday || !hiddenSet.has(dayKey(g.date)));
    }, [groupsWithVirtualToday, hiddenGroups, showHandled]);

    const hiddenStats = useMemo(() => {
        let meetings = 0;
        let trackedHrs = 0;
        hiddenGroups.forEach((g) => {
            g.items.forEach((m) => {
                if (m.cancelled) return;
                meetings += 1;
                if (meetingState(m) === "tracked") {
                    const ms = new Date(m.end).getTime() - new Date(m.start).getTime();
                    if (Number.isFinite(ms) && ms > 0) trackedHrs += ms / 3600000;
                }
            });
        });
        return { days: hiddenGroups.length, meetings, trackedHrs };
    }, [hiddenGroups]);

    if (calendarStatus.loaded && (!hasPermission("canSeeInternal") || calendarStatus.optOut)) {
        return <Navigate to="/" replace />;
    }

    return (
        <ContainerStyled maxWidth="lg">
            <PageHeader
                title="Meetings"
                subtitle={!isLoading && !hasError && items.length > 0 ? (
                    <>
                        {items.length} in range · {stats.untracked} need time recorded
                    </>
                ) : null}
                filters={
                    <FiltersBar>
                        <ChipRow>
                            <ChipLabel>Category</ChipLabel>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={category}
                                onChange={(e, value) => value && setCategory(value)}
                                aria-label="Filter meetings by category"
                            >
                                {CATEGORY_OPTIONS.map((opt) => (
                                    <ToggleButton key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </ChipRow>
                        <ChipRow>
                            <ChipLabel>State</ChipLabel>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={tracked}
                                onChange={(e, value) => value && setTracked(value)}
                                aria-label="Filter meetings by tracking state"
                            >
                                {TRACKED_OPTIONS.map((opt) => (
                                    <ToggleButton key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </ChipRow>
                        <DateRow>
                            <DateField>
                                <AppDatePicker label="From" size="small" value={from} onChange={(v) => setFrom(v)} />
                            </DateField>
                            <DateField>
                                <AppDatePicker label="To" size="small" value={to} onChange={(v) => setTo(v)} />
                            </DateField>
                            <Tooltip title="Past meetings show with a warning rule when untracked, dimmed when tracked. Future meetings are subtle. Today's day section sticks while you scroll.">
                                <AutoScrollHint>
                                    <ScheduleIcon/> auto-scrolls to now
                                </AutoScrollHint>
                            </Tooltip>
                        </DateRow>
                    </FiltersBar>
                }
            />

            {!isLoading && !hasError && items.length > 0 && (
                <StatStrip>
                    <StatCard size="compact" tone={stats.untracked > 0 ? "warn" : "muted"} label="Untracked" value={stats.untracked} sub="need time recorded" />
                    <StatCard
                        size="compact"
                        tone="success"
                        label="Tracked"
                        value={stats.trackedCount}
                        sub={stats.trackedHours > 0 ? `${stats.trackedHours.toFixed(1)} hrs captured` : "no hours yet"}
                    />
                    <StatCard size="compact" tone="info" label="Elsewhere" value={stats.elsewhereCount} sub="tracked elsewhere" />
                    <StatCard size="compact" tone="muted" label="Cancelled" value={stats.cancelledCount} sub="no time owed" />
                </StatStrip>
            )}

            {hasError && <ErrorBanner role="alert">{meetingsState.error}</ErrorBanner>}

            {isLoading && (
                <Box>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <SkeletonRow key={i}>
                            <Skeleton height={18} width="55%" />
                            <Skeleton height={12} width="35%" />
                        </SkeletonRow>
                    ))}
                </Box>
            )}

            {!isLoading && !hasError && items.length === 0 && (
                <EmptyStateBox>
                    <EventIcon />
                    <Typography variant="body1">No meetings in this range</Typography>
                    <Typography variant="caption">Try widening the date filter or switching to "All" states.</Typography>
                </EmptyStateBox>
            )}

            {!isLoading && !hasError && items.length > 0 && (
                <Box>
                    {hiddenGroups.length > 0 && (
                        <HandledToggle onClick={() => setShowHandled((v) => !v)} role="button" aria-pressed={showHandled}>
                            <VisibilityOff />
                            {showHandled ? (
                                <>
                                    Hide <strong>{hiddenStats.days}</strong> fully-tracked past day
                                    {hiddenStats.days === 1 ? "" : "s"}
                                </>
                            ) : (
                                <>
                                    Show <strong>{hiddenStats.days}</strong> fully-tracked past day
                                    {hiddenStats.days === 1 ? "" : "s"} · <strong>{hiddenStats.meetings}</strong> meeting
                                    {hiddenStats.meetings === 1 ? "" : "s"}
                                    {hiddenStats.trackedHrs > 0 ? ` · ${hiddenStats.trackedHrs.toFixed(1)} hrs` : ""}
                                </>
                            )}
                        </HandledToggle>
                    )}
                    {visibleGroups.map((g) =>
                        g.virtualToday ? (
                            <DaySection key="__virtual_today__">
                                <DayHeader $tone="today">
                                    <DayLabel>
                                        <DayPrimary $tone="today">Today</DayPrimary>
                                        <DaySecondary>{dayDateLine(g.date)}</DaySecondary>
                                    </DayLabel>
                                    <DayHeaderRight>
                                        <DayCounts className="day-counts">nothing scheduled</DayCounts>
                                    </DayHeaderRight>
                                </DayHeader>
                                <NowDivider ref={nowAnchorRef}>
                                    <span className="now-label">
                                        <FiberManualRecord className="now-dot" /> Now · {formatTime(now)}
                                    </span>
                                    <span className="now-line" />
                                </NowDivider>
                            </DaySection>
                        ) : (
                            renderDayGroup(g)
                        ),
                    )}
                </Box>
            )}

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
                {menuMeeting && meetingState(menuMeeting) === "untracked" && (
                    <MenuItem onClick={() => handleManualTrack(menuMeeting, true)}>
                        <TrackedElsewhereIcon fontSize="small" /> Mark as tracked elsewhere
                    </MenuItem>
                )}
                {menuMeeting && meetingState(menuMeeting) === "elsewhere" && (
                    <MenuItem onClick={() => handleManualTrack(menuMeeting, false)}>
                        <UntrackedMenuIcon fontSize="small" /> Mark as untracked
                    </MenuItem>
                )}
            </Menu>

            <MeetingTimeEntryDialog open={timeDialogOpen} onClose={handleTimeDialogClose} meeting={selectedMeeting} />
        </ContainerStyled>
    );
};

export default Meetings;
